import { SERVICE_BASE_URLS } from "../global/constants";
import {
  IRebuildPlan,
  IRebuildResult,
  IRebuildVerify,
  ISpecAdoptResult,
  ISpecPlan,
} from "../types/api/restore.type";
import api from "./api";

const ROOT = `${SERVICE_BASE_URLS.DB_MANAGER}/db-environments`;

/** Read-only: the recorded spec against the cluster. */
const specPlan = () => api.get<ISpecPlan>(`${ROOT}/spec/plan`);

/**
 * Write the live spec into the record, for environments with no record or a stale one.
 *
 * Touches no Kubernetes object. This is the first-rollout backfill: until it has run, a
 * rebuild has nothing to rebuild from. `userId` is required because this acts across every
 * tenant, so the server records the caller rather than checking ownership.
 */
const specAdopt = (userId: string, envIds?: number[]) =>
  api.post<ISpecAdoptResult>(`${ROOT}/spec/adopt`, envIds?.length ? { env_ids: envIds } : {}, {
    headers: { "user-id": userId },
  });

/** Read-only: which environments can be rebuilt, and which cannot. */
const rebuildPlan = () => api.get<IRebuildPlan>(`${ROOT}/rebuild/plan`);

/**
 * Recreate missing databases from their recorded spec, under their original ids.
 *
 * Omitting `envIds` rebuilds the whole `missing` bucket — the DR path — and says so with an
 * explicit flag. An empty array means *nothing*: it used to fall through to "everything"
 * because both sides tested the list for truthiness, so a selection filtered down to zero
 * would have rebuilt the estate. The server refuses a request that says neither.
 */
const rebuild = (userId: string, envIds?: number[]) =>
  api.post<IRebuildResult>(
    `${ROOT}/rebuild`,
    envIds === undefined ? { all_missing: true } : { env_ids: envIds },
    { headers: { "user-id": userId } }
  );

/**
 * Clear the delete mark on an environment that is staying after all.
 *
 * A delete stamps the record before destroying anything, so one that fails partway leaves a
 * database the rebuild refuses to touch — otherwise "Rebuild all" would recreate something
 * someone had just asked to destroy. This is the operator saying it is staying. Accounts only;
 * no Kubernetes object is touched.
 */
const keepEnvironment = (userId: string, envId: number) =>
  api.post<{ env_id: number }>(
    `${ROOT}/${envId}/rebuild/keep`,
    {},
    { headers: { "user-id": userId } }
  );

/** Did the data come back, or did the pod merely restart? */
const verifyRebuild = (envId: number) =>
  api.get<IRebuildVerify>(`${ROOT}/${envId}/rebuild/verify`);

const RestoreService = {
  specPlan,
  specAdopt,
  rebuildPlan,
  rebuild,
  keepEnvironment,
  verifyRebuild,
};

export default RestoreService;
